import { Badge, Button, toast } from "@devhop/ui";
import { Cloud, Database, HardDrive, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  getAutoSyncStatus,
  runAutoSyncNow,
  subscribeAutoSyncStatus,
  type AutoSyncScope,
} from "../lib/offline-sync/auto-sync";
import {
  AUTO_SYNC,
  LOCAL_COMPACTION,
} from "../lib/offline-sync/offline-sync-policy";
import {
  compactSyncedLocalOrdersForShift,
  formatCompactionBytes,
  getLocalCompactionStats,
  type LocalCompactionResult,
  type LocalCompactionStats,
} from "../lib/offline-sync/local-compaction";
import { getStorageEstimate } from "@/shared/lib/persistent-storage";

type OfflineSyncDebugPanelProps = {
  scope: AutoSyncScope | null;
};

function formatIntervalMs(ms: number): string {
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  return `${Math.round(ms / 60_000)} min`;
}

export function OfflineSyncDebugPanel({ scope }: OfflineSyncDebugPanelProps) {
  const [autoStatus, setAutoStatus] = useState(getAutoSyncStatus);
  const [runningManual, setRunningManual] = useState(false);
  const [compactionStats, setCompactionStats] =
    useState<LocalCompactionStats | null>(null);
  const [lastCompaction, setLastCompaction] =
    useState<LocalCompactionResult | null>(null);
  const [storageUsage, setStorageUsage] = useState<{
    usage?: number;
    quota?: number;
    persisted: boolean;
  } | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [runningCompaction, setRunningCompaction] = useState(false);

  useEffect(() => subscribeAutoSyncStatus(setAutoStatus), []);

  const refreshStats = useCallback(async () => {
    if (!scope?.shiftId) {
      setCompactionStats(null);
      return;
    }
    setLoadingStats(true);
    try {
      const [stats, storage] = await Promise.all([
        getLocalCompactionStats(scope.shiftId),
        getStorageEstimate(),
      ]);
      setCompactionStats(stats);
      setStorageUsage(storage);
    } finally {
      setLoadingStats(false);
    }
  }, [scope?.shiftId]);

  useEffect(() => {
    void refreshStats();
  }, [refreshStats, autoStatus.lastRunAt]);

  const handleForceSync = async () => {
    if (!scope) return;
    setRunningManual(true);
    try {
      const result = await runAutoSyncNow("manual", scope);
      if (result && result.synced > 0) {
        toast.success(`sync ສຳເລັດ ${result.synced} ລາຍການ`);
      } else if (result && result.failed > 0) {
        toast.warning(`sync ລົ້ມເຫຼວ ${result.failed} ລາຍການ`);
      } else {
        toast.info("ບໍ່ມີລາຍການຄ້າງ sync");
      }
      await refreshStats();
    } finally {
      setRunningManual(false);
    }
  };

  const handleCompact = async (dryRun: boolean) => {
    if (!scope?.shiftId) return;
    setRunningCompaction(true);
    try {
      const result = await compactSyncedLocalOrdersForShift(scope.shiftId, {
        dryRun,
      });
      setLastCompaction(result);
      if (dryRun) {
        toast.info(
          `ທົດລອງ: ຈະ compact ${result.compacted} ລາຍການ (~${formatCompactionBytes(result.bytesEstimateFreed)})`,
        );
      } else if (result.compacted > 0) {
        toast.success(
          `compact ${result.compacted} order ແລ້ວ (~${formatCompactionBytes(result.bytesEstimateFreed)})`,
        );
      } else {
        toast.info("ບໍ່ມີ order ທີ່ພ້ອມ compact");
      }
      await refreshStats();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "compact ລົ້ມເຫຼວ",
      );
    } finally {
      setRunningCompaction(false);
    }
  };

  const last = autoStatus.lastResult;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Cloud className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-semibold">Background auto-sync</h2>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={autoStatus.running ? "secondary" : "outline"}>
              {autoStatus.running ? "ກຳລັງ sync" : "ພ້ອມ"}
            </Badge>
            {autoStatus.pendingReason ? (
              <Badge variant="outline">
                debounce: {autoStatus.pendingReason}
              </Badge>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!scope || autoStatus.running || runningManual}
              onClick={() => void handleForceSync()}
            >
              {(autoStatus.running || runningManual) && (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              )}
              Sync ທັນທີ
            </Button>
          </div>
        </div>
        <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <dt className="text-muted-foreground">interval</dt>
            <dd>{formatIntervalMs(AUTO_SYNC.intervalMs)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">debounce ຈ່າຍ/adjust</dt>
            <dd>{formatIntervalMs(AUTO_SYNC.debounceMsByReason.after_complete)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">ຄັ້ງລ່າສຸດ</dt>
            <dd className="truncate">{autoStatus.lastRunAt ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">ຜົນລ່າສຸດ</dt>
            <dd>
              {last
                ? `ok ${last.synced} / fail ${last.failed}`
                : autoStatus.lastSkipReason ?? autoStatus.lastError ?? "—"}
            </dd>
          </div>
        </dl>
      </div>

      <div className="rounded-lg border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-semibold">Local compaction</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!scope || runningCompaction || loadingStats}
              onClick={() => void refreshStats()}
            >
              {loadingStats && (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              )}
              ໂຫຼດສະຖິຕິ
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!scope || runningCompaction}
              onClick={() => void handleCompact(true)}
            >
              ທົດລອງ
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!scope || runningCompaction}
              onClick={() => void handleCompact(false)}
            >
              {runningCompaction && (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              )}
              Compact ຕອນນີ້
            </Button>
          </div>
        </div>
        <p className="mt-2 text-muted-foreground text-xs">
          ຫຼັງ sync ສຳເລັດ ≥{" "}
          {formatIntervalMs(LOCAL_COMPACTION.minAgeMs)} — ລຶບ cart snapshot /
          KDS local ເພື່ອຫຼຸດຂະໜາດ IndexedDB (ຂໍ້ມູນຂາຍຢູ່ server)
        </p>
        {compactionStats ? (
          <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-5">
            <div>
              <dt className="text-muted-foreground">local orders</dt>
              <dd>{compactionStats.totalOrders}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">synced</dt>
              <dd>{compactionStats.syncedOrders}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">compact ແລ້ວ</dt>
              <dd>{compactionStats.compactedOrders}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">ພ້ອມ compact</dt>
              <dd>{compactionStats.eligibleToCompact}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">sync ຄ້າງ</dt>
              <dd>{compactionStats.pendingSync}</dd>
            </div>
          </dl>
        ) : null}
        {lastCompaction ? (
          <p className="mt-2 text-xs">
            ລ່າສຸດ ({lastCompaction.dryRun ? "dry-run" : "run"}): compact{" "}
            {lastCompaction.compacted}/{lastCompaction.eligible},{" "}
            ~{formatCompactionBytes(lastCompaction.bytesEstimateFreed)}, ops -{" "}
            {lastCompaction.operationsDeleted}, KDS -{" "}
            {lastCompaction.kitchenTicketsDeleted}
          </p>
        ) : null}
        {storageUsage ? (
          <div className="mt-3 flex items-center gap-2 text-muted-foreground text-xs">
            <HardDrive className="h-3.5 w-3.5" />
            <span>
              origin storage:{" "}
              {storageUsage.usage != null
                ? formatCompactionBytes(storageUsage.usage)
                : "—"}{" "}
              /{" "}
              {storageUsage.quota != null
                ? formatCompactionBytes(storageUsage.quota)
                : "—"}
              {storageUsage.persisted ? " (persisted)" : " (best-effort)"}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
